// внимание! установка языка должна быть ДО всех импортов,
// т.к. импортируемые модули могут использовать переводы
import './service/Initi18n'; // tslint:disable-next-line ordered-imports
import * as Cookie from 'js-cookie';
import * as React from 'react';
import { ReactElement, ReactNode } from 'react';
import * as ReactDOM from 'react-dom';

import { QueryExecutor } from './api/QueryExecutor';
import { RecommendationsMutationExecutor } from './api/recommendations/RecommendationsMutationExecutor';
import { BannersPriceEditor } from './components/BannersPriceEditor/BannersPriceEditor';
import { CampaignTypesList } from './components/CampaignTypesList/CampaignTypesList';
import { DisplayMapProvider } from './components/DisplayMapProvider/DisplayMapProvider';
import { ExperimentsProvider } from './components/ExperimentsProvider/ExperimentsProvider';
import { FeedbackTooltip } from './components/FeedbackTooltip/FeedbackTooltip';
import { FirstCampaignTizer } from './components/FirstCampaignTizer/FirstCampaignTizer';
import './components/FixedDataTable/FixedDataTable';
import { FreelancerInfoPanel } from './components/FreelancerInfoPanel/FreelancerInfoPanel';
import './components/Lego/Lego';
import { MobileAppsCreateSingle } from './components/MobileAppsCreateSingle/MobileAppsCreateSingle';
import { OutdoorMap } from './components/OutdoorMap/OutdoorMap';
import { Root } from './components/Root/Root';
import { WalletSwitchOffLimitEditPopupWrapper } from './components/WalletSwitchOffLimitEditPopupWrapper/WalletSwitchOffLimitEditPopupWrapper';
import { getDNAApolloClient } from './declarations/apollo';
import { FiltersForHistotyOfChanges } from './declarations/log-filters/log-filters';
import './index.css';
import { getCSRFToken } from './service/ApiHelpers/ApiHelpers';
import {
    captureException,
    initErrorsReporting
} from './service/ErrorsReporting/ErrorsReporting';
import { serializeFilterToString } from './service/FilterHelpers/FilterHelpers';
import { HeaderHelpers } from './service/HeaderHelpers/HeaderHelpers';

import { BalanceCart } from './components/BalanceCart/BalanceCart';
import {
    createTextAdGroup,
    getGroupsMutationExecutor
} from './service/mutationHelpers/mutationHelpers';
import { PcodeLoader, PcodeRenderer } from './service/Pcode/Pcode';
import { RUM } from './service/RUM/RUM';
import { CampaignType } from './typings/domain';
import {
    CurrencyCode,
    executeRecommendationMutation
} from './typings/graphql-grid';
import { GridScreenUrlSearchQueryParams } from './typings/grid';
import { RecommendationKey } from './typings/recommendations';
import './vars.css';

// функция создания "безопасного" элемента, см. DIRECT-83385
export type createSafeElementType = <P extends {}>(
    componentClass: React.ComponentClass<P>,
    props: P,
    children: ReactNode[]
) => ReactElement<P>;

const isDevelopmentEnv = process.env.NODE_ENV === 'development';
const isDirect = process.env.REACT_APP_TARGET === 'direct';
const win = window as Window;
win.PcodeLoader = PcodeLoader;
win.PcodeRenderer = PcodeRenderer;

RUM.init();
RUM.sendTTIData();
initErrorsReporting(isDevelopmentEnv);

if (!isDirect) {
    const params = new URLSearchParams(window.location.search);
    const cookieLogin = Cookie.get('yandex_login');
    const isPublicDomain =
        !cookieLogin && window.location.href.includes('freelancers');
    const login =
        params.get(GridScreenUrlSearchQueryParams.ulogin) ||
        cookieLogin ||
        (isPublicDomain ? 'yuber' : null);

    if (login === null) {
        throw new Error('login must be defined');
    }

    Root.initStore({
        login,
        isPublicDomain,
        officeContacts: {
            support: {
                landline: [['+7 (495) 739-37-77', 0, '']],
                regional: [['8 800 234-24-80', 0, '']]
            },
            email: 'adv@yandex-team.ru'
        },
        availableCampaignsTypes: [
            CampaignType.TEXT,
            CampaignType.PERFORMANCE,
            CampaignType.MOBILE_CONTENT,
            CampaignType.MCBANNER,
            CampaignType.DYNAMIC,
            CampaignType.CPM_DEALS,
            CampaignType.CPM_BANNER,
            CampaignType.CPM_YNDX_FRONTPAGE
        ],
        welcomeParams: {
            country: '208',
            currency: CurrencyCode.RUB
        }
    });

    ReactDOM.render(
        <Root
            login={login}
            enableRecommendations={true}
            gridAllowed={true}
            rmpEnabled={true}
            isWebvisorEnabled={false}
            dealsEnabled={true}
            domainsReportEnabled={true}
            isHeaderAvailable={true}
            dealsReadonly={false}
            enableSidebar={true}
            minusPhraseLibraryEnabled={true}
            isCampaignUpdateAllowed={true}
        />,
        document.getElementById('root')
    );

    // @ts-ignore
    win.dna = { components: { Root } };
} else {
    const reportExportedComponentError = (
        error: Error,
        errorInfo?: object
    ): void => {
        captureException(error, { extra: errorInfo });
    };

    const safeComponentHOC = <P extends {}>(
        componentClass: React.ComponentClass<P>
    ): React.ComponentClass<P> => {
        if (componentClass === undefined) {
            return class EmptyComponent extends React.Component<P> {
                componentDidMount(): void {
                    reportExportedComponentError(
                        new Error('undefined component render')
                    );
                }
                render(): ReactNode {
                    return null;
                }
            };
        }

        return class extends componentClass {
            componentDidCatch(error: Error, errorInfo: object): void {
                reportExportedComponentError(error, errorInfo);
            }
        };
    };

    const safeCreateElement: createSafeElementType = <P extends {}>(
        componentClass: React.ComponentClass<P>,
        props: P,
        children: ReactNode[]
    ): React.ReactElement<P> => {
        return React.createElement<P>(
            safeComponentHOC(componentClass),
            props,
            children
        );
    };

    win.dna = {
        reactDOMRender: ReactDOM.render.bind(ReactDOM),
        reactDOMUnmount: ReactDOM.unmountComponentAtNode.bind(ReactDOM),
        reactCreateElement: safeCreateElement,
        utils: {
            getQueryExecutor: (login: string): QueryExecutor => {
                const dnaClient = getDNAApolloClient(login, getCSRFToken);
                return new QueryExecutor(dnaClient);
            },
            getGroupsMutationExecutor: getGroupsMutationExecutor,
            serializeLogFilterToString: serializeFilterToString,
            filtersForHistotyOfChanges: FiltersForHistotyOfChanges,
            createTextAdGroup: createTextAdGroup,
            executeRecommendation: (
                login: string,
                keys: RecommendationKey[]
            ): Promise<executeRecommendationMutation> => {
                const dnaClient = getDNAApolloClient(login, getCSRFToken);
                const recommendationExecutor = new RecommendationsMutationExecutor(
                    dnaClient
                );

                return recommendationExecutor.executeRecommendation(keys);
            }
        },
        components: {
            Root: Root,
            BalanceCart: BalanceCart,
            FeedbackTooltip: FeedbackTooltip,
            FreelancerInfoPanel: FreelancerInfoPanel,
            CampaignTypesList: CampaignTypesList,
            ExperimentsControl: ExperimentsProvider, // TODO: Для обратной совместимости, удалить когдай выкатится DIRECT-79148
            ExperimentsProvider: ExperimentsProvider,
            OutdoorMap: OutdoorMap,
            // Generator: Generator,
            DisplayMapProvider: DisplayMapProvider,
            FirstCampaignTizer: FirstCampaignTizer,
            MobileAppsCreateSingle: MobileAppsCreateSingle,
            WalletSwitchOffLimitEditPopupWrapper: WalletSwitchOffLimitEditPopupWrapper,
            BannersPriceEditor: BannersPriceEditor
        },
        helpers: {
            HeaderHelpers: HeaderHelpers
        }
    };
}
